/**
 * XMLUI App Component
 * 
 * This is the root XMLUI component that provides the main layout structure
 * for the MoneyMojo application shell.
 * 
 * Features:
 * - Semantic HTML structure
 * - Accessibility compliance (WCAG 2.2 AA)
 * - Responsive layout
 * - Theme support
 * - Focus management
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React, { ReactNode } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface AppProps {
  children: ReactNode;
}

/**
 * Main App Layout Component
 * 
 * Provides the semantic HTML structure for the application shell
 * with proper accessibility landmarks and responsive design.
 */
export function App({ children }: AppProps): React.ReactElement {
  const { theme } = useTheme();

  return (
    <div 
      className={`min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200 ${theme}`}
      role="application"
      aria-label="MoneyMojo Personal Finance Application"
    >
      {/* Main application container */}
      <div className="flex min-h-screen flex-col min-w-0">
        {/* Header and Navigation are rendered here by children */}
        {children}
      </div>
    </div>
  );
}
